import {
  datamuseQuery,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";

/**
 * The Describing Words Finder's two questions, both answered by Datamuse from
 * adjective–noun pairs counted in Google Books Ngrams:
 *
 * - `rel_jjb` — the adjectives most often used to modify a noun: evidence →
 *   empirical, strong, sufficient;
 * - `rel_jja` — the nouns an adjective most often modifies: significant →
 *   difference, role, effect.
 *
 * Datamuse returns each list ordered by how often the pair occurs, which is the
 * default order here; the alternative is alphabetical.
 */

export type DescribeMode = "adjectives" | "nouns";
export type DescribeSort = "common" | "alphabetical";

const MAX_RESULTS = 150;

const PARAM: Readonly<Record<DescribeMode, "rel_jjb" | "rel_jja">> = {
  adjectives: "rel_jjb",
  nouns: "rel_jja",
};

export function describingQuery(word: string, mode: DescribeMode): string {
  return datamuseQuery({ [PARAM[mode]]: word, md: "pf", max: MAX_RESULTS });
}

/** Which mode an answered query was asked in. */
export function modeOfQuery(query: string): DescribeMode | null {
  const params = new URLSearchParams(query);
  if (params.has(PARAM.adjectives)) {
    return "adjectives";
  }
  return params.has(PARAM.nouns) ? "nouns" : null;
}

/** The searched word, whichever mode the query was asked in. */
export function wordOfQuery(query: string): string {
  const params = new URLSearchParams(query);
  return params.get(PARAM.adjectives) ?? params.get(PARAM.nouns) ?? "";
}

/** The answer as a word list: repeats and the searched word removed, then sorted. */
export function describingWords(
  word: string,
  results: readonly WordResult[],
  sort: DescribeSort
): string[] {
  const searched = word.toLowerCase();
  const words: string[] = [];
  const seen = new Set<string>([searched]);
  for (const result of results) {
    const key = result.word.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      words.push(result.word);
    }
  }
  return sort === "alphabetical"
    ? words.sort((a, b) => a.localeCompare(b, "en"))
    : words;
}

/** The pairing as it would appear in a sentence: adjective first. */
export function pairing(
  word: string,
  result: string,
  mode: DescribeMode
): string {
  return mode === "adjectives" ? `${result} ${word}` : `${word} ${result}`;
}
